import { RadioField, RadioGroupField, Stack } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useField } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { PostingSearchCriteriaShipperSearchDTO } from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { ShipperDTO } from '../../data/LoadboardShipperAPI';
import { ShipperAutocomplete } from './ShipperAutocomplete';

function mapShipperSearchToShipper(
  shipper: PostingSearchCriteriaShipperSearchDTO,
): ShipperDTO {
  return {
    guid: shipper.guid,
    name: shipper.display_name,
    city: '',
    state: '',
  };
}

function mapShipperToShipperSearch(
  shipper: ShipperDTO,
): PostingSearchCriteriaShipperSearchDTO {
  return { guid: shipper.guid, display_name: shipper.name };
}

type ShippersFilterType = 'include' | 'exclude';

interface Props {
  disabled?: boolean;
}

export function ShippersFilter({ disabled }: Props) {
  const typeRef = useRef<ShippersFilterType>('include');
  const [includeField, , includeHelpers] = useField<
    PostingSearchCriteriaShipperSearchDTO[]
  >({
    name: 'search_criteria.shipper.include',
  });

  const [excludeField, excludeMeta, excludeHelpers] = useField<
    PostingSearchCriteriaShipperSearchDTO[]
  >({
    name: 'search_criteria.shipper.exclude',
    validate: (value: PostingSearchCriteriaShipperSearchDTO[]) => {
      if (typeRef.current === 'exclude' && value.length === 0) {
        return 'Select shipper to continue';
      }

      return undefined;
    },
  });

  const [type, setType] = useState<ShippersFilterType>(() =>
    excludeField.value.length > 0 ? 'exclude' : 'include',
  );
  const value = type === 'exclude' ? excludeField.value : includeField.value;

  useEffect(() => {
    typeRef.current = type;
  }, [type]);

  return (
    <Stack>
      <RadioGroupField
        name="type"
        value={type}
        fullWidth={true}
        disabled={disabled}
        RadioGroupProps={{ row: true }}
        onChange={(event) => {
          const newType = event.target.value;

          if (newType === 'exclude') {
            void excludeHelpers.setValue(value);
            void includeHelpers.setValue([]);
          } else {
            void includeHelpers.setValue(value);
            void excludeHelpers.setValue([]);
          }

          setType(newType as ShippersFilterType);
        }}
      >
        <RadioField label="Include" value="include" disabled={disabled} />
        <RadioField label="Exclude" value="exclude" disabled={disabled} />
      </RadioGroupField>

      <Box paddingLeft="large">
        <ShipperAutocomplete
          type={type}
          disabled={disabled}
          error={excludeMeta.touched && excludeMeta.error}
          value={value.map(mapShipperSearchToShipper)}
          onChange={(newValue) => {
            const newFilter = newValue.map(mapShipperToShipperSearch);

            if (type === 'exclude') {
              void excludeHelpers.setValue(newFilter);
            } else {
              void includeHelpers.setValue(newFilter);
            }
          }}
        />
      </Box>
    </Stack>
  );
}
